import { Row } from '@tanstack/react-table';
import { Column } from '@tanstack/react-table';
import { DEFAULT_MIN_COLUMN_SIZE } from '../constants';
import {
  getTableColumnPinLeft,
  getTableColumnPinRight,
} from './column-resizing';

export const DEFAULT_MIN_PAGE = 1;

export const SHADOW_GRADIENT_WIDTH = 12;

interface GetRowShadowOptionsProps {
  isFirstSubRow: boolean;
  isLastSubRow: boolean;
}

interface RowStylingOptions<T> {
  row: Row<T>;
  index: number;
  rows: Row<T>[];
}

const getRowShadowOptions = ({
  isFirstSubRow,
  isLastSubRow,
}: GetRowShadowOptionsProps): string | undefined => {
  if (isFirstSubRow && isLastSubRow)
    return 'shadow-[inset_0_1px_0_hsl(var(--muted-foreground)),inset_0_-1px_0_hsl(var(--muted-foreground))]';
  if (isFirstSubRow)
    return 'shadow-[inset_0_1px_0_hsl(var(--muted-foreground))]';
  if (isLastSubRow)
    return 'shadow-[inset_0_-1px_0_hsl(var(--muted-foreground))]';

  return undefined;
};

export const getOffset = <T>({
  column,
  leftLeafColumns,
  rightLeafColumns,
  side,
}: {
  column: Column<T>;
  leftLeafColumns: Column<T>[];
  rightLeafColumns: Column<T>[];
  side: 'left' | 'right';
}) => {
  const columns = side === 'left' ? leftLeafColumns : rightLeafColumns;
  const index = columns.findIndex(col => col.id === column.id);

  if (index === -1) return 0;

  return columns
    .slice(...(side === 'left' ? [0, index] : [index + 1]))
    .reduce((sum, col) => sum + col.getSize(), 0);
};

export const getRowStyling = <T>({
  row,
  index,
  rows,
}: RowStylingOptions<T>): string | undefined => {
  const isSubRow = row.depth > 0;
  if (!isSubRow) return undefined;

  const previousRow = rows[index - 1];
  const nextRow = rows[index + 1];

  const isFirstSubRow = index === 0 || previousRow.depth < row.depth;
  const isLastSubRow = index === rows.length - 1 || nextRow.depth < row.depth;

  return getRowShadowOptions({ isFirstSubRow, isLastSubRow });
};

export const isRowWithId = <T>(obj: T): obj is { id: string } & T =>
  typeof obj === 'object' && obj && 'id' in obj && typeof obj.id === 'string';

export const getRowId = <T>(row: T, index?: number) => {
  const isRowObject = typeof row === 'object' && row;
  const id = isRowObject && 'id' in row ? row.id : index;

  const name =
    isRowObject && 'name' in row ? String((row as { name: string }).name) : '';
  const nameHash = name ? `-${name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';

  return `${String(id)}${nameHash}`;
};

export const getTableColumnPinningProperties = <T>(column: Column<T>) => {
  const pinPosition = column.getIsPinned();
  const isLeftPinned = pinPosition === 'left';
  const isRightPinned = pinPosition === 'right';

  const shouldApplyShadow =
    (isLeftPinned && column.getIsLastColumn('left')) ||
    (isRightPinned && column.getIsFirstColumn('right'));

  const style = {
    ...(isLeftPinned && { left: getTableColumnPinLeft(column.id) }),
    ...(isRightPinned && { right: getTableColumnPinRight(column.id) }),
  };

  return {
    pinPosition,
    isLeftPinned,
    isRightPinned,
    shouldApplyShadow,
    style,
  };
};

export const mapColumnToColumnWithMinSize = <
  T extends { size?: number; minSize?: number },
>(
  col: T
): T => {
  const size = col.size;

  if (typeof size !== 'number') return col;

  const derivedMin = Math.min(DEFAULT_MIN_COLUMN_SIZE, size);
  const minSize = typeof col.minSize === 'number' ? col.minSize : derivedMin;

  return {
    ...col,
    minSize,
  } as T;
};
